import { Head, Link, router, useForm } from '@inertiajs/react'
import { useState } from 'react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import DashboardLayout from '@/components/DashboardLayout'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'

interface UnpaidLesson {
  id: number
  lesson_date: string
  start_time: string
  end_time: string
  student: { id: number; name: string }
  students_count: number
  unit_teacher_payment: number
  teacher_payment: number
  formatted_teacher_payment: string
  description: string | null
}

interface Teacher {
  id: number
  name: string
  unpaid_lessons: UnpaidLesson[]
}

interface Payment {
  id: number
  teacher: Teacher
  total_amount: number
  formatted_total: string
  status: string
  payment_date: string | null
  lessons_count: number
  notes: string | null
  created_at: string
}

interface PaginationInfo {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Props {
  payments: Payment[]
  teachers: Teacher[]
  pagination: PaginationInfo
  filters: {
    teacher_id?: string
    status: string
  }
}

const statusConfig = {
  pending: { label: 'Pendente', variant: 'secondary' as const },
  paid: { label: 'Pago', variant: 'default' as const },
  cancelled: { label: 'Cancelado', variant: 'destructive' as const },
}

export default function TeacherPaymentsIndex({
  payments,
  teachers,
  pagination,
  filters,
}: Props) {
  const [selectedTeacher, setSelectedTeacher] = useState(
    filters.teacher_id || 'all',
  )
  const [selectedStatus, setSelectedStatus] = useState(filters.status)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedTeacherId, setSelectedTeacherId] = useState<number | null>(
    null,
  )
  const [selectedLessons, setSelectedLessons] = useState<number[]>([])
  const [notes, setNotes] = useState('')

  const selectedTeacherData = teachers.find((t) => t.id === selectedTeacherId)
  const selectedLessonsData =
    selectedTeacherData?.unpaid_lessons.filter((l) =>
      selectedLessons.includes(l.id),
    ) || []
  const totalAmount = selectedLessonsData.reduce(
    (sum, lesson) => sum + lesson.teacher_payment,
    0,
  )

  const handleFilterChange = (teacherId: string, status: string) => {
    const params: any = { page: 1 }
    if (teacherId !== 'all') params.teacher_id = teacherId
    if (status !== 'all') params.status = status
    router.get('/teacher_payments', params, { preserveState: true })
  }

  const handlePageChange = (page: number) => {
    const params: any = { page }
    if (selectedTeacher !== 'all') params.teacher_id = selectedTeacher
    if (selectedStatus !== 'all') params.status = selectedStatus
    router.get('/teacher_payments', params, { preserveState: true })
  }

  const handleCreatePayment = () => {
    if (!selectedTeacherId || selectedLessons.length === 0) return

    router.post(
      '/teacher_payments',
      {
        lesson_ids: selectedLessons,
        notes: notes,
      },
      {
        onSuccess: () => {
          setDialogOpen(false)
          setSelectedTeacherId(null)
          setSelectedLessons([])
          setNotes('')
        },
      },
    )
  }

  const toggleLesson = (lessonId: number) => {
    setSelectedLessons((prev) =>
      prev.includes(lessonId)
        ? prev.filter((id) => id !== lessonId)
        : [...prev, lessonId],
    )
  }

  const selectAllLessons = () => {
    if (!selectedTeacherData) return
    setSelectedLessons(selectedTeacherData.unpaid_lessons.map((l) => l.id))
  }

  const deselectAllLessons = () => {
    setSelectedLessons([])
  }

  return (
    <DashboardLayout>
      <Head title="Pagamentos de Professores" />

      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Pagamentos de Professores</h1>
            <p className="text-muted-foreground mt-1">
              Gerencie os pagamentos dos instrutores. Para consultar quanto cada
              professor produziu num período, veja{' '}
              <Link
                href="/reports/lessons_given"
                className="underline underline-offset-2 hover:text-foreground"
              >
                Aulas Dadas
              </Link>
              .
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button>Novo Pagamento</Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Criar Pagamento de Professor</DialogTitle>
                <DialogDescription>
                  Selecione o professor e as aulas que deseja incluir no
                  pagamento
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                {/* Seleção de Professor */}
                <div className="space-y-2">
                  <Label>Professor</Label>
                  <Select
                    value={selectedTeacherId?.toString() || ''}
                    onValueChange={(value) => {
                      setSelectedTeacherId(Number(value))
                      setSelectedLessons([])
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um professor" />
                    </SelectTrigger>
                    <SelectContent>
                      {teachers.map((teacher) => (
                        <SelectItem
                          key={teacher.id}
                          value={teacher.id.toString()}
                        >
                          {teacher.name} ({teacher.unpaid_lessons.length} aulas
                          pendentes)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Lista de Aulas */}
                {selectedTeacherData && (
                  <>
                    {selectedTeacherData.unpaid_lessons.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        Este professor não possui aulas pendentes de pagamento
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-center">
                          <Label>
                            Aulas Pendentes (
                            {selectedTeacherData.unpaid_lessons.length})
                          </Label>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={selectAllLessons}
                            >
                              Selecionar Todas
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={deselectAllLessons}
                            >
                              Limpar Seleção
                            </Button>
                          </div>
                        </div>

                        <div className="border rounded-md">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-12"></TableHead>
                                <TableHead>Data</TableHead>
                                <TableHead>Horário</TableHead>
                                <TableHead>Aluno</TableHead>
                                <TableHead>Valor</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {selectedTeacherData.unpaid_lessons.map(
                                (lesson) => (
                                  <TableRow key={lesson.id}>
                                    <TableCell>
                                      <Checkbox
                                        checked={selectedLessons.includes(
                                          lesson.id,
                                        )}
                                        onCheckedChange={() =>
                                          toggleLesson(lesson.id)
                                        }
                                      />
                                    </TableCell>
                                    <TableCell>
                                      {format(
                                        new Date(lesson.lesson_date),
                                        'dd/MM/yyyy',
                                        { locale: ptBR },
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      {lesson.start_time} - {lesson.end_time}
                                    </TableCell>
                                    <TableCell>
                                      {lesson.student
                                        ? lesson.student.name
                                        : ''}
                                      {lesson.students_count > 1 && (
                                        <span className="ml-1 text-xs text-muted-foreground">
                                          +{lesson.students_count - 1} aluno(s)
                                        </span>
                                      )}
                                    </TableCell>
                                    <TableCell className="font-semibold">
                                      {lesson.formatted_teacher_payment}
                                      {lesson.students_count > 1 && (
                                        <span className="block text-xs font-normal text-muted-foreground">
                                          R${' '}
                                          {lesson.unit_teacher_payment.toFixed(
                                            2,
                                          )}{' '}
                                          × {lesson.students_count}
                                        </span>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ),
                              )}
                            </TableBody>
                          </Table>
                        </div>

                        {/* Total */}
                        <div className="flex justify-end items-center gap-2 text-lg font-bold">
                          <span>Total:</span>
                          <span>R$ {totalAmount.toFixed(2)}</span>
                        </div>

                        {/* Observações */}
                        <div className="space-y-2">
                          <Label>Observações (opcional)</Label>
                          <Textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Adicione observações sobre este pagamento..."
                            rows={3}
                          />
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleCreatePayment}
                  disabled={!selectedTeacherId || selectedLessons.length === 0}
                >
                  Criar Pagamento
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filtros */}
        <div className="flex gap-4 mb-6">
          <div className="w-64">
            <Select
              value={selectedTeacher}
              onValueChange={(value) => {
                setSelectedTeacher(value)
                handleFilterChange(value, selectedStatus)
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos os professores" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os professores</SelectItem>
                {teachers.map((teacher) => (
                  <SelectItem key={teacher.id} value={teacher.id.toString()}>
                    {teacher.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="w-48">
            <Select
              value={selectedStatus}
              onValueChange={(value) => {
                setSelectedStatus(value)
                handleFilterChange(selectedTeacher, value)
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="pending">Pendente</SelectItem>
                <SelectItem value="paid">Pago</SelectItem>
                <SelectItem value="cancelled">Cancelado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Tabela */}
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Professor</TableHead>
                <TableHead>Aulas</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Data Pagamento</TableHead>
                <TableHead>Criado em</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={7}
                    className="text-center py-8 text-muted-foreground"
                  >
                    Nenhum pagamento encontrado
                  </TableCell>
                </TableRow>
              ) : (
                payments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell className="font-medium">
                      {payment.teacher.name}
                    </TableCell>
                    <TableCell>{payment.lessons_count}</TableCell>
                    <TableCell className="font-semibold">
                      {payment.formatted_total}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          statusConfig[
                            payment.status as keyof typeof statusConfig
                          ].variant
                        }
                      >
                        {
                          statusConfig[
                            payment.status as keyof typeof statusConfig
                          ].label
                        }
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {payment.payment_date
                        ? format(new Date(payment.payment_date), 'dd/MM/yyyy', {
                            locale: ptBR,
                          })
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {format(
                        new Date(payment.created_at),
                        'dd/MM/yyyy HH:mm',
                        { locale: ptBR },
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild variant="outline" size="sm">
                        <Link href={`/teacher_payments/${payment.id}`}>
                          Ver Detalhes
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Paginação */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-muted-foreground">
              Mostrando {payments.length} de {pagination.total_count} pagamentos
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={pagination.current_page === 1}
              >
                Anterior
              </Button>
              <div className="flex items-center gap-2">
                {Array.from(
                  { length: pagination.total_pages },
                  (_, i) => i + 1,
                ).map((page) => (
                  <Button
                    key={page}
                    variant={
                      page === pagination.current_page ? 'default' : 'outline'
                    }
                    size="sm"
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Button>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={pagination.current_page === pagination.total_pages}
              >
                Próxima
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  )
}
